import type { HistoricalTimelineDecade } from "../generated-types";
import { historicalTimelineService } from "../services/historicalTimelineService";
import { getEventImages, getValidImageUrl } from "./timeline-utils";

const decadesByDataSourceId = new Map<string, HistoricalTimelineDecade[]>();
const loadPromisesByDataSourceId = new Map<string, Promise<HistoricalTimelineDecade[]>>();

export async function loadTimelineDecades(dataSourceId: string): Promise<HistoricalTimelineDecade[]> {
	const cachedDecades = decadesByDataSourceId.get(dataSourceId);
	if (cachedDecades) {
		return cachedDecades;
	}

	const inFlight = loadPromisesByDataSourceId.get(dataSourceId);
	if (inFlight) {
		return inFlight;
	}

	const loadPromise = historicalTimelineService
		.getHistoricalTimeline(dataSourceId)
		.then((response) => {
			const decades = response?.data?.decades ?? [];
			decadesByDataSourceId.set(dataSourceId, decades);
			prefetchTimelineImages(decades);
			return decades;
		})
		.finally(() => {
			loadPromisesByDataSourceId.delete(dataSourceId);
		});

	loadPromisesByDataSourceId.set(dataSourceId, loadPromise);
	return loadPromise;
}

export function prefetchTimelineImages(decades: HistoricalTimelineDecade[]): void {
	const imageUrls = new Set<string>();

	for (const decade of decades) {
		const decadeImage = getValidImageUrl(decade.image);
		if (decadeImage) {
			imageUrls.add(decadeImage);
		}

		for (const year of decade.years ?? []) {
			for (const event of year.events ?? []) {
				for (const { imageUrl } of getEventImages(event)) {
					imageUrls.add(imageUrl);
				}
			}
		}
	}

	imageUrls.forEach((url) => {
		const image = new Image();
		image.decoding = "async";
		image.src = url;
	});
}
